import React, { useState } from 'react';
import MatchCard from './MatchCard';
import Load from './Load';
import { FaseGrupoPartidasLite, PartidaLite } from '../services/faseGrupoPartidasService';

interface FaseChaveLite {
  id: string;
  nome: string;
  ordem: number;
  partidas?: PartidaLite[];
}

interface CategoryJogosTabProps {
  grupos: FaseGrupoPartidasLite[];
  fasesChaveOrdenadas: FaseChaveLite[];
  gruposLoading: boolean;
  showGroupPhase: boolean;
  faseGrupo: { id: string } | null;
  error?: boolean;
}

const CategoryJogosTab: React.FC<CategoryJogosTabProps> = ({
  grupos,
  fasesChaveOrdenadas,
  gruposLoading,
  showGroupPhase,
  faseGrupo,
  error,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'grupos' | 'eliminatorias'>('grupos');

  const hasGroupMatches = grupos.some(g => g.partidas.length > 0);
  const hasEliminationMatches = fasesChaveOrdenadas.some(f => (f.partidas || []).length > 0);
  const hasAnyMatches = hasGroupMatches || hasEliminationMatches;

  const hasGroupPhase = showGroupPhase && faseGrupo != null && grupos.length > 0;
  const hasEliminationPhase = fasesChaveOrdenadas.length > 0;
  const showSubTabs = hasGroupPhase && hasEliminationPhase;

  // Mostra a fase de grupos sempre que ela existe (mesmo sem partidas registradas),
  // para que o organizador veja a estrutura de grupos logo após a geração.
  const showGroupSection = hasGroupPhase;

  const renderGroupPhase = () => (
    <div>
      {grupos.map((grupo) => (
        <div key={grupo.id} className="mb-3">
          <h5 className="fw-600 mb-2">{grupo.nome}</h5>
          {grupo.partidas.length > 0 ? (
            <div className="d-flex flex-column gap-2">
              {grupo.partidas.map((partida) => (
                <MatchCard key={partida.id} partida={partida} showHeader={false} />
              ))}
            </div>
          ) : (
            <p className="text-muted small">Nenhuma partida neste grupo</p>
          )}
        </div>
      ))}
    </div>
  );

  const renderEliminationPhase = () => (
    <div>
      {fasesChaveOrdenadas.map((fase) => (
        <div key={fase.id} className="mb-4">
          <h4 className="fw-700 mb-3">{fase.nome}</h4>
          {(fase.partidas || []).length > 0 ? (
            <div className="d-flex flex-column gap-2">
              {(fase.partidas || []).map((partida) => (
                <MatchCard key={partida.id} partida={partida} showHeader={false} />
              ))}
            </div>
          ) : (
            <p className="text-muted small">Nenhuma partida nesta fase</p>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {gruposLoading && <Load />}

      {!gruposLoading && error && (
        <div className="alert alert-danger" role="alert">
          Erro ao carregar fase de grupos.
        </div>
      )}

      {!gruposLoading && !error && !hasAnyMatches && !showGroupSection && (
        <div className="text-center py-4">
          <p className="text-muted">Nenhuma partida cadastrada</p>
        </div>
      )}

      {!gruposLoading && !error && (hasAnyMatches || showGroupSection) && showSubTabs && (
        <div>
          {/* Sub-tabs Navigation */}
          <ul className="nav nav-tabs flex-wrap mb-3" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeSubTab === 'grupos' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveSubTab('grupos')}
                role="tab"
                aria-selected={activeSubTab === 'grupos'}
              >
                Fase de Grupos
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeSubTab === 'eliminatorias' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveSubTab('eliminatorias')}
                role="tab"
                aria-selected={activeSubTab === 'eliminatorias'}
              >
                Fases Eliminatórias
              </button>
            </li>
          </ul>

          {/* Sub-tabs Content */}
          <div className="tab-content">
            <div
              className={`tab-pane fade ${activeSubTab === 'grupos' ? 'show active' : ''}`}
              role="tabpanel"
            >
              {renderGroupPhase()}
            </div>
            <div
              className={`tab-pane fade ${activeSubTab === 'eliminatorias' ? 'show active' : ''}`}
              role="tabpanel"
            >
              {renderEliminationPhase()}
            </div>
          </div>
        </div>
      )}

      {/* No sub-tabs: only group phase */}
      {!gruposLoading && !error && (hasAnyMatches || showGroupSection) && !showSubTabs && hasGroupPhase && (
        <div className="mb-4">
          <h4 className="fw-700 mb-3">Fase de Grupos</h4>
          {renderGroupPhase()}
        </div>
      )}

      {/* No sub-tabs: only elimination phases */}
      {!gruposLoading && !error && hasAnyMatches && !showSubTabs && !hasGroupPhase && hasEliminationPhase && (
        renderEliminationPhase()
      )}
    </div>
  );
};

export default CategoryJogosTab;
